import {type PlayerColor} from '@colonist/game-states'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints.ts'
import {type UIReplayController} from './UIReplayController.ts'
import {ClientUserState} from '../ClientUserState.ts'
import {getReplayUrlSlug, REPLAY_URL_ACTION_SEARCH_PARAM_NAME} from './ReplayUrlUtilities.ts'
import {createLinkIn} from '../ui_utils/DomUtils.ts'
import {type UIGameController} from '../ui_game/UIGameController.ts'
import {type BigId} from '@colonist/types'
import {socket, uiGameManager} from '../EntryShared.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'

export class UIReplayShareController {
    static async shareOrCopyLink(gameController: UIGameController, replayController?: UIReplayController) {
        const getShareableLink = replayController
            ? () => UIReplayShareController.getShareableLinkInReplay(replayController)
            : () => UIReplayShareController.getShareableLinkInGameEnd(gameController)

        const isMobileApp = UIUtilsEjsVariables.isUserOnMobileAppOrWeb
        if(isMobileApp) {
            const link = await getShareableLink()
            if(link == undefined) return

            uiGameManager.openShareLinkMobileModal(link)
        } else {
            await UIReplayShareController.copyShareableLink(getShareableLink)
        }
    }

    private static async getShareableLinkInReplay(replayController: UIReplayController): Promise<string | undefined> {
        const databaseGameId = replayController.replayData.databaseGameId
        const replayPerspectiveColor = UIReplayShareController.getShareableLinkPerspectiveInReplay(replayController)
        const link = await UIReplayShareController.getShareableLinkWithPerspective(databaseGameId, replayPerspectiveColor)
        if(link == undefined) return undefined

        const currentActionIndex = replayController.getCurrentAction()
        return UIReplayShareController.addActionToShareableLink(link, currentActionIndex)
    }

    private static async getShareableLinkInGameEnd(gameController: UIGameController): Promise<string | undefined> {
        const databaseGameId = socket.gameListener.databaseGameId
        const replayPerspectiveColor = UIReplayShareController.getShareableLinkPerspectiveInGameEnd(gameController)
        return await UIReplayShareController.getShareableLinkWithPerspective(databaseGameId, replayPerspectiveColor)
    }

    private static async getShareableLinkWithPerspective(databaseGameId: BigId, perspective: PlayerColor): Promise<string | undefined> {
        const replayShareableLinkUrl = UIAPIEndpoints.apiReplayShareableLink(databaseGameId, perspective.toString())
        const replayShareableLinkResponse = await UIAjaxWrapper.getRequestHandler(replayShareableLinkUrl, undefined)
        if(replayShareableLinkResponse == undefined) return undefined
        return replayShareableLinkResponse.json
    }

    private static async copyShareableLink(getShareableLink: () => Promise<string | undefined>) {
        const TEXT_TYPE = 'text/plain'
        const getShareableLinkBlob = async() => {
            const isShareableLinkAlreadyInUrl = getReplayUrlSlug(window.location.href) !== undefined
            const shareableLink = isShareableLinkAlreadyInUrl ? location.href : await getShareableLink()
            if(shareableLink == undefined) return new Blob([''], {type: TEXT_TYPE})

            return new Blob([shareableLink], {type: TEXT_TYPE})
        }

        const shareableLinkClipboardItem = new ClipboardItem({
            [TEXT_TYPE]: getShareableLinkBlob(),
        })

        try {
            await navigator.clipboard.write([shareableLinkClipboardItem])
        } catch{
            const linkBlob = await shareableLinkClipboardItem.getType(TEXT_TYPE)
            const linkHref = await linkBlob.text()
            const hiddenLink = createLinkIn(document.body, linkHref, {key: 'strings:colonistCommon.utils.emptyString'}, '')
            hiddenLink.target = '_blank'
            hiddenLink.click()
        }
    }

    private static getShareableLinkPerspectiveInReplay(replayController: UIReplayController): PlayerColor {
        const playerUserStates = replayController.replayData.playerUserStates
        const replayPerspectiveColor = replayController.replayData.playerPerspective
        const replayPerspectivePlayerUserState = playerUserStates.find((playerUserState) => playerUserState.selectedColor === replayPerspectiveColor)
        const isSelectedPerspectivePlayedAsByMe = ClientUserState.userState?.id === replayPerspectivePlayerUserState?.userId
        if(isSelectedPerspectivePlayedAsByMe) return replayPerspectiveColor

        const anyPlayerUserStatePlayedAsByMe = playerUserStates.find((playerUserState) => playerUserState.userId === ClientUserState.userState?.id)
        if(anyPlayerUserStatePlayedAsByMe !== undefined) return anyPlayerUserStatePlayedAsByMe.selectedColor

        return replayPerspectiveColor
    }

    private static getShareableLinkPerspectiveInGameEnd(gameController: UIGameController): PlayerColor {
        const myColor = gameController.myColor
        const playOrder = gameController.playOrder

        const isMyColorSpectator = gameController.isPlayerSpectator
        if(isMyColorSpectator) return playOrder[0]

        return myColor
    }

    private static addActionToShareableLink(link: string, action: number): string {
        const isActionGameBeginning = action === -1
        if(isActionGameBeginning) return link

        const url = new URL(link)
        url.searchParams.set(REPLAY_URL_ACTION_SEARCH_PARAM_NAME, action.toString())
        return url.href
    }
}
